import React, {useEffect, useMemo, useState} from 'react';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {getLocaleContent} from '@helper/locale-content.js';
import UserService from '@services/user.service';
import {getSlugId, getSlugType, getUuidFromSlug} from '@helper/string.helper';
import PopupAppointment from './PopupAppointment';
import BookingSuccess from '../Home/BookingSuccess';
import {safeHtml} from '@helper/sanitize-html';

function AccordionSection({title, children}) {
    const [open, setOpen] = useState(true);

    return (
        <div className="doctor-details__section">
            <div className="doctor-details__section-header" onClick={() => setOpen(!open)}>
                <h2 className="doctor-details__section-title">{title}</h2>
                <img
                    src="/bw_image/chevron-up-doctor-details.png"
                    alt="toggle"
                    className={`doctor-details__icon ${open ? 'rotate' : ''}`}
                />
            </div>

            <div className={`doctor-details__content ${open ? 'open' : 'collapsed'}`}>{children}</div>
        </div>
    );
}

export default function SpecialistDoctorDetails(props) {
    const router = useRouter();
    const {isReady, query, locale} = router;

    const id = useMemo(() => {
        if (!isReady) return null;
        const raw = query?.slug;
        const str = (Array.isArray(raw) ? raw[0] : raw || '').trim();
        if (!str) return null;
        const uuid = getUuidFromSlug(str);
        if (uuid) return uuid;
    }, [isReady, query?.slug]);

    const [loading, setLoading] = useState(false);
    const [doctor, setDoctor] = useState(() => props?.preloadedDoctor || null);
    const [error, setError] = useState('');

    // State cho modal đặt lịch khám
    const [showAppointment, setShowAppointment] = useState(false);
    // State quản lý modal thành công
    const [showSuccess, setShowSuccess] = useState(false);
    const [bookingCode, setBookingCode] = useState('');

    useEffect(() => {
        if (props?.preloadedDoctor) {
            setDoctor(props.preloadedDoctor);
            return;
        }
        if (!id) return;
        const svc = new UserService();
        let cancelled = false;

        setLoading(true);
        setError('');

        (async () => {
            try {
                const res = await svc.getUserDetail(id, {locale});
                const payload = res?.data?.data ?? res?.data ?? res;
                if (!cancelled) setDoctor(payload || null);
            } catch (e) {
                console.error(e);
                if (!cancelled) setError(getLocaleContent(locale, 'page.specialist-doctor.doctor-not-found'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [id, locale, props?.preloadedDoctor]);

    const metaDesc = useMemo(() => {
        const raw = doctor?.about_me_short || '';
        return typeof raw === 'string' ? raw.replace(/<[^>]*>/g, '').slice(0, 150) : '';
    }, [doctor]);

    if (!isReady && !doctor)
        return <div style={{padding: 40}}>{getLocaleContent(locale, 'page.specialist-doctor.page-loading')}</div>;
    if (loading && !doctor)
        return <div style={{padding: 40}}>{getLocaleContent(locale, 'page.specialist-doctor.detail-loading')}</div>;
    if (error) return <div style={{padding: 40, color: 'red'}}>{error}</div>;
    if (!doctor)
        return <div style={{padding: 40}}>{getLocaleContent(locale, 'page.specialist-doctor.doctor-not-found')}</div>;

    return (
        <div className="doctor-details">
            {/* Header */}
            <div className="doctor-details__header">
                <img src={doctor.image_url} alt={doctor.full_name || 'Doctor'} className="doctor-details__avatar" />
                <div className="doctor-details__info">
                    <div className="doctor-details__info_title">
                        <h1 className="doctor-details__name">
                            {doctor.education_level_name && doctor.full_name
                                ? `${doctor.education_level_name} - ${doctor.full_name}`
                                : doctor.full_name || getLocaleContent(locale, 'page.specialist-doctor.doctor-label')}
                        </h1>
                        <div className="doctor-details__position">
                            <img src="/bw_image/hospital-details.png" alt="position" />
                            <p>
                                {doctor.position_name ||
                                    getLocaleContent(locale, 'page.specialist-doctor.doctor-label')}
                            </p>
                        </div>
                        <div className="doctor-details__department">
                            <img src="/bw_image/ic-department.png" alt="department" />
                            <p>
                                {doctor.department_name ||
                                    getLocaleContent(locale, 'page.specialist-doctor.specialty-label')}
                            </p>
                        </div>
                        <div className="doctor-details__expertise">
                            <img src="/bw_image/stethoscope-details.png" alt="expertise" />
                            <p>{doctor.expertise || ''}</p>
                        </div>
                    </div>
                    <div className="doctor-details__info_desc">
                        <p className="doctor-details__intro">
                            {doctor.about_me_short ||
                                doctor.about_me ||
                                getLocaleContent(locale, 'page.specialist-doctor.about-updating')}
                        </p>
                        <div className="doctor-details__btn-ap">
                            <button className="doctor-details__btn" onClick={() => setShowAppointment(true)}>
                                <img src="/bw_image/appoinment-dotor-details.png" alt="appointment" />
                                {getLocaleContent(locale, 'page.specialist-doctor.book-appointment')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="doctor-details-collap">
                <AccordionSection title={getLocaleContent(locale, 'page.specialist-doctor.introduction')}>
                    <div
                        dangerouslySetInnerHTML={safeHtml(
                            doctor.about_me
                                ? doctor.about_me.replace(/\n/g, '<br />')
                                : getLocaleContent(locale, 'page.specialist-doctor.about-updating'),
                        )}
                    />
                </AccordionSection>

                <AccordionSection title={getLocaleContent(locale, 'page.specialist-doctor.expertise')}>
                    {doctor.user_work_experiences && doctor.user_work_experiences.length > 0 ? (
                        <>
                            {doctor.user_work_experiences.map((exp, index) => (
                                <div
                                    key={exp.work_experience_id || index}
                                    dangerouslySetInnerHTML={safeHtml(exp.content || exp.position || '')}
                                />
                            ))}
                        </>
                    ) : (
                        <p>{getLocaleContent(locale, 'page.specialist-doctor.work-experience-updating')}</p>
                    )}
                </AccordionSection>

                <AccordionSection title={getLocaleContent(locale, 'page.specialist-doctor.training')}>
                    {doctor.user_educations && doctor.user_educations.length > 0 ? (
                        <>
                            {doctor.user_educations.map((edu, index) => (
                                <div
                                    key={edu.education_user_id || index}
                                    dangerouslySetInnerHTML={safeHtml(
                                        edu.content ||
                                            `${edu.education_level_name || ''} ${edu.training_center || ''} ${
                                                edu.specialized || ''
                                            }`,
                                    )}
                                />
                            ))}
                        </>
                    ) : (
                        <p>{getLocaleContent(locale, 'page.specialist-doctor.training-updating')}</p>
                    )}
                </AccordionSection>

                <AccordionSection title={getLocaleContent(locale, 'page.specialist-doctor.achievements')}>
                    {doctor.user_achievements && doctor.user_achievements.length > 0 ? (
                        <>
                            {doctor.user_achievements.map((achievement, index) => (
                                <div
                                    key={achievement.achievement_user_id || index}
                                    dangerouslySetInnerHTML={safeHtml(
                                        achievement.content || achievement.description || achievement.tittle || '',
                                    )}
                                />
                            ))}
                        </>
                    ) : (
                        <p>{getLocaleContent(locale, 'page.specialist-doctor.achievements-updating')}</p>
                    )}
                </AccordionSection>
                <div className="doctor-details__btn-pr">
                    <button className="doctor-details__btn" onClick={() => setShowAppointment(true)}>
                        <img src="/bw_image/appoinment-dotor-details.png" />
                        {getLocaleContent(locale, 'page.specialist-doctor.book-appointment')}
                    </button>
                </div>
            </div>

            {/* Modal đặt lịch khám */}
            {showAppointment && (
                <PopupAppointment
                    onClose={() => {
                        setShowAppointment(false);
                    }}
                    selectedDoctor={doctor}
                    onSuccess={code => {
                        setBookingCode(code);
                        setShowSuccess(true);
                    }}
                />
            )}

            {/* Modal thành công */}
            <BookingSuccess
                open={showSuccess}
                bookingCode={bookingCode}
                onClose={() => {
                    setShowSuccess(false);
                    setBookingCode('');
                }}
            />
        </div>
    );
}
